import { z } from 'zod'
import { parseWidgetConfig } from '@homerunner-next/widget-core/schema'
import { galleryParamsSchema } from './data'
import { widgetSchema } from '../../common/schema'
import { lenientBoolDefault, lenientObjectGroup } from '../../common/zod-lenient'

/** The toolkit caps the tour headings at 120 characters. */
const GALLERY_TITLE_MAX = 120
/** Room order / overrides text: rooms.ts reads 100 lines; this only bounds a pasted blob. */
const GALLERY_LINES_MAX = 10_000

/** A heading leaf: non-strings and blanks degrade to the default; trimmed and capped. */
const lenientTitle = (def: string) =>
  z.preprocess((v) => {
    if (typeof v !== 'string') return undefined
    const t = v.trim().slice(0, GALLERY_TITLE_MAX)
    return t === '' ? undefined : t
  }, z.string().default(def))

/** Newline-separated text (one entry per line); non-strings degrade to ''. Kept as typed: rooms.ts parses it. */
const lenientLines = () =>
  z.preprocess((v) => (typeof v === 'string' ? v.slice(0, GALLERY_LINES_MAX) : undefined), z.string().default(''))

// Hero-grid layout (#1706, toolkit property-gallery 3.255.0). The ranges are the
// toolkit's (Admin/PropertyGallery/Page.php MAIN_SHARE_*, GAP_MAX, RADIUS_MODES).
// The DEFAULTS are the platform's CURRENT render (50/50 columns, 8px gap, every
// tile rounded), not the toolkit's 37.5 / 3 / outer, so a feed that never sets
// these keys renders byte-identically to before.
export const GALLERY_HERO_DEFAULTS = { mainShare: 50, gap: 8, radiusMode: 'all' } as const
export const GALLERY_MAIN_SHARE_RANGE = { min: 25, max: 75 } as const
export const GALLERY_GAP_MAX = 60
export const GALLERY_RADIUS_MODES = ['outer', 'all'] as const

// Out-of-range or non-numeric input falls back to the default (the toolkit's
// clamp_main_share / clamp_px rule) instead of throwing: a strict parse would
// blank the whole widget at render time. Written as preprocess over a plain
// number leaf so the zod-form UI keeps rendering a number field.
const inRangeOrDefault = (min: number, max: number, fallback: number) => (v: unknown) => {
  if (v === undefined || v === null || v === '') return undefined // -> schema default
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  return Number.isFinite(n) && n >= min && n <= max ? n : fallback
}

export const galleryWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Photo Gallery').describe('Section title text'),
  }).default({ show: false, title: 'Photo Gallery' }).describe('Section heading shown above the widget content'),
  style: z.enum(['simple', 'masonry']).default('masonry').describe('The predefined style of the gallery'),
  imageBorderRadius: z.number().default(8).describe('The border radius of the images in pixels'),
  mainShare: z.preprocess(
    inRangeOrDefault(GALLERY_MAIN_SHARE_RANGE.min, GALLERY_MAIN_SHARE_RANGE.max, GALLERY_HERO_DEFAULTS.mainShare),
    z.number().min(GALLERY_MAIN_SHARE_RANGE.min).max(GALLERY_MAIN_SHARE_RANGE.max).default(GALLERY_HERO_DEFAULTS.mainShare),
  ).describe('How much of the hero grid the large photo takes, in percent (25-75); the four smaller photos share the rest. Applies to the Masonry style on desktop'),
  gap: z.preprocess(
    inRangeOrDefault(0, GALLERY_GAP_MAX, GALLERY_HERO_DEFAULTS.gap),
    z.number().min(0).max(GALLERY_GAP_MAX).default(GALLERY_HERO_DEFAULTS.gap),
  ).describe('Gap between the hero photos, in pixels (0-60). The mobile carousel and the full-screen browser are not affected'),
  radiusMode: z.preprocess(
    (v) => (typeof v === 'string' && (GALLERY_RADIUS_MODES as readonly string[]).includes(v) ? v : undefined),
    z.enum(GALLERY_RADIUS_MODES).default(GALLERY_HERO_DEFAULTS.radiusMode),
  ).describe('Where the image border radius applies. Outer corners round the gallery as one block; Every photo rounds each tile, so the gaps between photos get rounded edges too'),
  filter: galleryParamsSchema,
  carousel: z.object({
    imagesToShow: z.number().min(1).max(6).default(1).describe('The number of images to show in the carousel'),
    imagesToShow780: z.number().min(1).max(6).default(2).describe('The number of images to show in the carousel at 780px in mobile first design'),
    imagesToShow1200: z.number().min(1).max(6).default(4).describe('The number of images to show in the carousel at 1200px in mobile first design'),
  }).default({
    imagesToShow: 1,
    imagesToShow780: 2,
    imagesToShow1200: 3,
  }).describe('The carousel settings of the widget'),
  lazyLoading: z.boolean().default(true).describe('Whether to enable lazy loading for images'),
  showAllPhotoButton: z.boolean().default(true).describe('Whether to show the "Show all photos" button'),
  /**
   * Photos grouped by room in the "Show all photos" view (#1694, toolkit Photo
   * Gallery "By room", 3.266.0–3.297.0). Needs a room on each photo from the
   * public API (central#3703); until it ships, and for any listing without
   * rooms, the view is the plain gallery whatever these say. The hero grid
   * and the carousel keep the gallery order either way. Every leaf is lenient
   * and defaults to prior behaviour, so an old stored blob re-parses as-is.
   */
  rooms: lenientObjectGroup(z.object({
    groupByRoom: lenientBoolDefault(false)
      .describe('In the "Show all photos" view, list the photos one section per room (rooms come from the PMS); the hero grid keeps the gallery order'),
    roomOrder: lenientLines()
      .describe('Rooms that should lead, one room name or room type (BEDROOM, FULL_BATHROOM) per line; the rest follow in gallery order'),
    roomOverrides: lenientLines()
      .describe('Headings to change, one rule per line: the room name or room id, an equals sign, the heading guests see; rooms given the same heading show as one section'),
    pinIndex: lenientBoolDefault(true)
      .describe('Keep a row of room names under the header while the guest scrolls the photo tour, with the room in view marked'),
    tourTitle: lenientTitle('Photo tour')
      .describe('Heading of the room index at the top of the photo tour'),
    moreTitle: lenientTitle('More photos')
      .describe('Heading of the closing group: photos that have no room'),
  }).default({}).describe('Photos grouped by room in the "Show all photos" view (photo tour)')),
  /** The single-photo view. */
  lightbox: lenientObjectGroup(z.object({
    zoom: lenientBoolDefault(true)
      .describe('Pinch, ctrl + scroll, the zoom button or the + and - keys zoom a photo in the lightbox'),
  }).default({}).describe('Lightbox (single photo view) behaviour')),
})

export type GalleryWidgetConfig = z.infer<typeof galleryWidgetConfig>

/**
 * Lift keys the runtime's settings merge grafted onto an array.
 *
 * Dead since widget-core 0.12.5 (#1703): getFinalSettings now replaces a
 * stored `[]` group with the override object, so nothing reaching
 * parseGalleryConfig is grafted any more. Remove in a gallery-scoped
 * follow-up; the history below says why it existed.
 *
 * The dashboard stores an untouched settings group as `{}`, which PHP's
 * json_encode serializes as `[]`. `getFinalSettings` merges a per-embed
 * `data-hr-options` override on top of the stored settings with lodash, and
 * lodash merges an object source INTO an existing array destination: the
 * override's keys land as own properties of that `[]`. Every group parser
 * (phpObject, lenientObjectGroup) then sees an Array and falls back to the
 * defaults, so the override is silently dropped. Lifting the non-index keys
 * back into a plain object restores the override; an array with index keys
 * only (a real list, or a bare `[]`) is left for the parsers' own handling.
 */
export function liftGraftedGroups(options: unknown): unknown {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return options
  const out: Record<string, unknown> = { ...(options as Record<string, unknown>) }
  for (const [key, value] of Object.entries(out)) {
    if (!Array.isArray(value)) continue
    const grafted = Object.keys(value).filter((k) => !/^\d+$/.test(k))
    if (grafted.length === 0) continue
    const bag = value as unknown as Record<string, unknown>
    out[key] = Object.fromEntries(grafted.map((k) => [k, bag[k]]))
  }
  return out
}

/** THE way the gallery parses its stored/merged settings: the grafted-group lift, then the canonical parse. */
export function parseGalleryConfig(options: unknown): GalleryWidgetConfig {
  return parseWidgetConfig(galleryWidgetConfig, liftGraftedGroups(options))
}
